'use client'

import { MouseEvent, useCallback, useMemo, useState } from 'react'

import {
  Message as MessageType,
  MessagePhotoModel,
} from '@marketplace-web/messaging/conversation-messaging-data'
import { useAbTest } from '@marketplace-web/feature-flags/ab-tests-data'

import PhotoMessage from '../../../../PhotoMessage'
import PhotosDialog from '../PhotosDialog'

type Props = {
  message: MessageType
  isLast: boolean
  isReadByOppositeUser: boolean
  isCurrentAuthor: boolean
  createdTime: string
}

const PhotoMessages = ({
  createdTime,
  isCurrentAuthor,
  isLast,
  isReadByOppositeUser,
  message,
}: Props) => {
  const imageOpeningInConversationExperiment = useAbTest('dd_tx_image_opening_in_conversation')
  const isImageOpeningInConversationExperimentOn =
    imageOpeningInConversationExperiment?.variant === 'on'

  const [photoDialogSlide, setPhotoDialogSlide] = useState<number | null>(null)

  const closePhotoDialog = useCallback(() => setPhotoDialogSlide(null), [])
  const openPhotoDialog = useCallback((index: number) => setPhotoDialogSlide(index), [])
  const handleSlideInteract = useCallback((slide: number) => setPhotoDialogSlide(slide), [])

  const visiblePhotos = useMemo(
    () => message.photos.filter(photo => !photo.isHidden),
    [message.photos],
  )

  return (
    <>
      {message.photos.map((photo: MessagePhotoModel, index: number) => {
        const isLastAndRead = isLast && isReadByOppositeUser && index + 1 === message.photos.length

        const handleThumbnailClick = isImageOpeningInConversationExperimentOn
          ? (event: MouseEvent<HTMLAnchorElement>) => {
              event.preventDefault()
              openPhotoDialog(index)
            }
          : undefined

        return (
          <PhotoMessage
            key={index}
            photo={photo}
            isCurrentAuthor={isCurrentAuthor}
            isLastAndRead={isLastAndRead}
            isLast={isLast}
            showTime={index === 0}
            createdTime={createdTime}
            messageId={message.id}
            onThumbnailClick={handleThumbnailClick}
          />
        )
      })}
      {isImageOpeningInConversationExperimentOn && visiblePhotos.length > 0 && (
        <PhotosDialog
          onClose={closePhotoDialog}
          onSlideInteract={handleSlideInteract}
          slide={photoDialogSlide}
          photos={visiblePhotos}
        />
      )}
    </>
  )
}

export default PhotoMessages
